import { Injectable } from '@angular/core';
import { IFetchOptions } from '@pnp/common';
import { SpHttpClient } from '../auth';
import { SearchResultItem } from '../services/models';
import { ChannelByAcronym, ReferenceDataService } from '../services/reference-data.service';
import { environment } from 'src/environments/environment';

function getPropertyNames(obj: string, index: number): string {
  if (obj) {
    const objArray = obj.split("#");
    return objArray[index];
  }
  return undefined;
}

let channelByAcronym: ChannelByAcronym = undefined;

function getChannelTitle(channelAcronym: string): string {
  const channel = channelByAcronym && channelByAcronym[channelAcronym];
  return channel && channel.Title;
}

function getItemTitle(
  HTAcommonTA: string, HTAcommonIndication: string,
  HTAcommonAssets: string, HTAcommonCountry: string): string {
  const tokens: string[] = [];
  if (HTAcommonTA) tokens.push(HTAcommonTA);
  if (HTAcommonIndication) tokens.push(HTAcommonIndication);
  if (HTAcommonAssets) tokens.push(HTAcommonAssets);
  if (HTAcommonCountry) tokens.push(HTAcommonCountry)
  return tokens.join(' / ');
}



@Injectable({
  providedIn: 'root'
})
export class SpSearchService {

  constructor(private referenceDataService: ReferenceDataService, private http: SpHttpClient) {}

  async postquery(queryText: string): Promise<SearchResultItem[]> {
    const body = {
      request: {
        __metadata: { type: 'Microsoft.Office.Server.Search.REST.SearchRequest' },
        Querytext: "*" + queryText + "*",
        RowLimit: 4900,
        TrimDuplicates: true,
        SelectProperties: {
          results: ['ID', 'HitHighlightedSummary', 'IsDocument', 'ModifiedBy', 'CreatedBy',
          'LastModifiedTime', 'Title', 'Modified', 'ModifiedOWSDATE', 'HTASearchMetadata', 'Created',
          'Author', 'OriginalPath', 'Modified', 'FileExtension', 'Path', 'SPWebURL', 'TA',
          'Asset', 'Indication', 'FileLeafRef', 'HTAcommonAssets', 'HTAcommonTA',
          'HTAcommonIndication', 'HTACustomHyperLink', 'HTACommonSearchMetadata', 'HTAcommonCountry',
          'OriginalPath', 'ServerRedirectedEmbedUrl', 'DefaultEncodingURL', 'LinkFilename', 'Filename']
        }
      }
    };
    const headers = new Headers({
      "odata-version": "3.0",
      "accept": "application/json;odata=verbose",
      "content-type": "application/json;odata=verbose;charset=utf-8"
    });
    const options: IFetchOptions = {
      body: JSON.stringify(body),
      headers: headers,
      method: "POST"
    };
    const response = await this.http.fetch(`${environment.siteUrl}/_api/search/postquery`, options);
    const responseObject = await response.json();
    if (responseObject.error) {
      throw responseObject.error;
    }
    const results: any[] = responseObject.d.postquery.PrimaryQueryResult.RelevantResults.Table.Rows.results;

    const items: SearchResultItem[] = [];
    if (results.length > 0) {
      for (let i = 0; i < results.length; i++) {
        const cells = results[i].Cells.results;
        const item = {};
        for (const cell of cells) {
          item[cell.Key] = cell.Value;
        }
        items.push(item);
      }
    }
    return items;
  }

  /*
 async searchQuery(queryText: string): Promise<SearchResultItem[]> {
  const searchQuery: ISearchQuery = {
    Querytext: queryText,
    RowLimit: 4900,
    TrimDuplicates: true,
    SelectProperties : [
      'ID', 'HitHighlightedSummary', 'IsDocument', 'ModifiedBy', 	'CreatedBy',
      'LastModifiedTime', 'Title', 'Modified', 'ModifiedOWSDATE',	'HTASearchMetadata',
      'Created', 'Author', 'OriginalPath', 'Modified', 'FileExtension', 'Path',
      'SPWebURL', 'TA', 'Asset', 'Indication', 'Country', 'FileLeafRef',
      'HTAcommonAssets', 'HTAcommonTA', 'HTAcommonIndication', 'HTACustomHyperLink',
      'HTACommonSearchMetadata', 'HTAcommonCountry', 'OriginalPath', 'ServerRedirectedEmbedUrl',
      'DefaultEncodingURL', 'LinkFilename', 'Filename'
    ]
  }
  const searchResult = await execute(() => sp.search(searchQuery));
  return searchResult.PrimarySearchResults as SearchResultItem[]
 }

 */

  async search(queryText: string): Promise<SearchResultItem[]> {
    const channelByAcronymPromise = this.referenceDataService.getChannelByAcronym();
    // const searchResultsPromise = this.searchQuery(queryText);
    const searchResultsPromise = this.postquery(queryText);
    channelByAcronym = await channelByAcronymPromise;
    const searchResultsItems = await searchResultsPromise;
    return this.GetSearchResultItems(searchResultsItems);
  }

  private setChannelAndUrl(item: SearchResultItem): SearchResultItem {
    const p = decodeURIComponent(item.Path).toLowerCase();
    let acronym: string;
    let url = item.Path;
    let isDocument = item.IsDocument as any === 'true' || item.IsDocument === true ? true : false;

    if (p.includes("/accesscatalog.aspx") || p.includes("/access evidence requirements")) {
      acronym = "AERC";
    }
    else if (p.includes("/pasthtadecisions.aspx") || p.includes("/pastdecisions")) {
      acronym = "PHD";
    }
    else if (p.includes("/aem guidance")) {
      acronym = "AEM";
    }
    else if (p.includes("/aem summaries")) {
      if (isDocument === false && item.FileExtension === "aspx") {
        url = item.DefaultEncodingURL;
        isDocument = true;
      }
      acronym = "AEM";
    }
    else if (p.includes("/esa guidance")) {
      acronym = "ESA";
    }
    else if (p.includes("/hsr guidance")) {
      acronym = "HSR";
    }
    else if (p.includes("/hta emc guidance")) {
      acronym = "HMC";
    }
    else if (p.includes("/eep guidance")) {
      acronym = "EEP";
    }
    else if (p.includes("/marketaccessanalysisguidance")) {
      acronym = "MAAP";
    }
    else if (p.includes("/tp1tp2guidance")) {
      acronym = "TP";
    }
    else if (p.includes("/hta informational material")) {
      if (isDocument === false && item.FileExtension === "aspx") {
        url = item.DefaultEncodingURL;
        isDocument = true;
      }
      acronym = "EduHTA";
    }
    else if (p.includes("/internal training materials")) {
      if (isDocument === false && item.FileExtension === "aspx") {
        url = item.DefaultEncodingURL;
        isDocument = true;
      }
      acronym = "EduHTA";
    }
    else if (p.includes("/external training materials")) {
      if (isDocument === false && item.FileExtension === "aspx") {
        url = item.DefaultEncodingURL;
        isDocument = true;
      }
      acronym = "EduHTA";
    }
    else if (p.includes("/hta trends")) {
      if (isDocument === false && item.FileExtension === "aspx") {
        url = item.DefaultEncodingURL;
        isDocument = true;
      }
      acronym = "HTAT";
    }
    // ********** CHANGE START
    //else if (p.includes("/acguidance") || p.includes("/achtadocuments") || p.includes("/achtadocumentssecure") || p.includes("/acotherdocuments") || p.includes("/acpolicydocuments") || p.includes("/acpolicydocumentssecure") || p.includes("/acnegotiationdocuments") || p.includes("/acnegotiationdocumentssecure")) {
    else if (p.includes("/acguidance")) {
      acronym = "AC";
    }
     
      /* start AccessHUb-3.15-Relese  */
    else if (p.includes("/maat evidence tracker files/")) {
      acronym = "MAET";
    }
    else if (p.includes("/faqquestions")) {
      acronym = "FAQ";
    }
    else if (p.includes("/marketaccessdatabases/")) {
      acronym = "MADB";
    }
    else if (p.includes("/hta economic model itcnma")) {
      acronym = "HMCNMA";
    }
    else if (p.includes("/contractrightguidance") || p.includes("/contractrightbestpracticeexamples")) {
      acronym = "CR";
    }
    else if (p.includes("/aerccountrycomparisondata")) {
      acronym = "AERC";
    }
    else if (p.includes("/achtafaqquestions")) {
      acronym = "ACHTAFAQ"; 
    }

      /* end AccessHUb-3.15-Relese  */
    // ********** CHANGE END
    else {
      //for attachment files or records which are not proper, it will not populate on the page.
      acronym = undefined;
    }
    if (acronym) {
      // const it = Object.assign({}, item);
      const it = item;
      it.IsDocument = isDocument;
      it.ChannelAcronym = acronym;
      it.Url = url;
      it.ChannelTitle = getChannelTitle(acronym);
      if (acronym == 'ACHTAFAQ') {
        it.ChannelTitle = 'HTA Community Learnings Tracker';
      }
      if(!it.ChannelTitle) {
        console.log(it);
      }
      return it;
    }
    return undefined;
  }

  private GetSearchResultItems(results: SearchResultItem[]): SearchResultItem[] {
    const items: SearchResultItem[] = [];
    let channel = "";
    let accountryID = "";
    let actab = "";
    let channel_full_name = "";
    let itemTitle = "";
    let id: number = undefined;
    for (let i = 0; i < results.length; i++) {
      const searchResultItem = results[i];


      if (searchResultItem.HTAcommonCountry === 'Armenia') {
        console.log(searchResultItem);
      }

      channel_full_name = "";
      channel = "";
      itemTitle = "";
      if (searchResultItem.HTACommonSearchMetadata) {

        channel = getPropertyNames(searchResultItem.HTACommonSearchMetadata, 0);
        id = +getPropertyNames(searchResultItem.HTACommonSearchMetadata, 1);

        if (channel === "AC") {
          if (searchResultItem.HTACommonSearchMetadata) {
            const objArray = searchResultItem.HTACommonSearchMetadata.split("#");
            actab = "HTA";
            if (objArray.length > 2)
            actab = getPropertyNames(searchResultItem.HTACommonSearchMetadata, 2);
            actab = (actab != undefined) ? actab : "HTA";

            accountryID = getPropertyNames(searchResultItem.HTACommonSearchMetadata, 3);
            accountryID = (accountryID != undefined)? accountryID : "0";
          }
        }

        channel_full_name = getChannelTitle(channel);
        itemTitle = getItemTitle(searchResultItem.HTAcommonTA, searchResultItem.HTAcommonIndication, searchResultItem.HTAcommonAssets, searchResultItem.HTAcommonCountry);

        //****/ special case for EEP session Topics *****//
        //ta/asset/ind is not present in document lib of EEP and topics list
        // so we cannot show it instead we are showing the title of the document
        let p = searchResultItem.Path;
        p = decodeURIComponent(p.toLowerCase());
        if (p.includes("/externalexpertsessiontopics") || p.includes("/external expert session attachments")) {
          itemTitle = searchResultItem.Title;
        }
        //****** special case ends *******//

        //only push if channel/id value is valid
        if (itemTitle) {
          // const item: SearchResultItem = Object.assign({}, searchResultItem);
          const item: SearchResultItem = searchResultItem;
          item.IsDocument = item.IsDocument as any === 'true' || item.IsDocument === true ? true : false;
          item.Created = item.Created && new Date(item.Created);
          item.ModifiedOWSDATE = item.ModifiedOWSDATE && new Date(item.ModifiedOWSDATE);
          item.ChannelAcronym = channel;
          item.ChannelTitle = channel_full_name;
          item.ItemTitle = itemTitle;
          item.ItemId = id;
          item.AcCountryId = +accountryID;
          item.Url = item.Path;
          item.AcCommunityTab = actab;
          item.IsGuidance = false;
          items.push(item);

          /*
          items.push({
            createdBy: item.Author ? item.Author : "",
            createdDate: new Date(item.Created),
            modifiedDate: new Date(item.ModifiedOWSDATE),
            docType: item.FileExtension ? item.FileExtension : "",
            name: item.Title ? item.Title : "",
            path: item.Path ? item.Path : "",
            type: item.FileType ? item.FileType : "",
            url: item.Path ? item.Path : "",
            channel: channel ? channel : "",
            channel_full_name: channel_full_name,
            ta: item.HTAcommonTA ? item.HTAcommonTA : "",
            asset: item.HTAcommonAssets ? item.HTAcommonAssets : "",
            indication: item.HTAcommonIndication ? item.HTAcommonIndication : "",
            summary: item.HitHighlightedSummary ? item.HitHighlightedSummary : "",
            IsDocument: item.IsDocument ? item.IsDocument : false,
            ItemTitle: itemTitle,
            Title: item.Title ? item.Title : "",
            itemID: id,
            originalPath: item.OriginalPath ? item.OriginalPath : "",
            ServerRedirectedEmbedUrl: item.ServerRedirectedEmbedUrl ? item.ServerRedirectedEmbedUrl : "",
            LinkFilename: item.LinkFilename ? item.LinkFilename : "",
            Filename: item.Filename ? item.Filename : "",
            DefaultEncodingURL: item.DefaultEncodingURL ? item.DefaultEncodingURL : "",
            ACCountryID: accountryID,
            HTACustomHyperLink: item.HTACustomHyperLink ? item.HTACustomHyperLink : "",
            ACCommunityTab: actab
          });
          */
        }

      }
      else {

        const item = this.setChannelAndUrl(searchResultItem);
        if (item && item.ChannelAcronym) {
          item.Created = item.Created && new Date(item.Created);
          item.ModifiedOWSDATE = item.ModifiedOWSDATE && new Date(item.ModifiedOWSDATE);
          item.ItemTitle = item.Title;
          item.ItemId = undefined;
         // if(item.ChannelAcronym === "MADB"){
         //   item.ItemId = id;
         // }
          item.AcCountryId = undefined;
          item.AcCommunityTab = undefined;
          const path = item.OriginalPath.toLowerCase();
          item.IsGuidance = item.IsDocument && (
            path.indexOf("access evidence requirements/") > -1
            || path.indexOf("guidance/") > -1
            || path.indexOf("hta informational material/") > -1
            || path.indexOf("training materials/") > -1
            || path.indexOf("aem summaries/") > -1 );
          items.push(item);
        }


        /*
        if (channel !== "")
          items.push({
            createdBy: item.Author ? item.Author : "",
            createdDate: new Date(item.Created),
            modifiedDate: new Date(item.ModifiedOWSDATE),
            docType: item.FileExtension ? item.FileExtension : "",
            name: item.Title ? item.Title : "",
            path: item.Path ? item.Path : "",
            type: item.FileType ? item.FileType : "",
            url: url,
            channel: channel ? channel : "",
            channel_full_name: channel_full_name,
            summary: item.HitHighlightedSummary ? item.HitHighlightedSummary : "",
            IsDocument: isdocument,
            ItemTitle: item.Title ? item.Title : "",
            Title: item.Title ? item.Title : "",
            originalPath: item.OriginalPath ? item.OriginalPath : "",
            ServerRedirectedEmbedUrl: item.ServerRedirectedEmbedUrl ? item.ServerRedirectedEmbedUrl : "",
            LinkFilename: item.LinkFilename ? item.LinkFilename : "",
            Filename: item.Filename ? item.Filename : "",
            DefaultEncodingURL: item.DefaultEncodingURL ? item.DefaultEncodingURL : "",
            itemID: -1,
            ta: item.HTAcommonTA ? item.HTAcommonTA : "",
            asset: item.HTAcommonAssets ? item.HTAcommonAssets : "",
            indication: item.HTAcommonIndication ? item.HTAcommonIndication : "",
            ACCountryID: "",
            HTACustomHyperLink: item.HTACustomHyperLink ? item.HTACustomHyperLink : "",
            ACCommunityTab: ""
          });

        */

      }
    }
    return items;
  }
}
